
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CampaignDetailsForm } from "./CampaignDetailsForm";
import { CampaignAudienceSelector } from "./CampaignAudienceSelector";
import { CampaignScheduler } from "./CampaignScheduler";
import { CampaignContentEditor } from "./CampaignContentEditor";
import { CampaignPreview } from "./CampaignPreview";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCampaigns } from "@/hooks/useCampaigns";

export interface CampaignData {
  id?: string;
  name: string;
  subject: string;
  previewText: string;
  templateId?: string;
  content?: string;
  audienceSegments: string[];
  excludedSegments: string[];
  selectedSubscriberIds?: string[];
  sendDate?: Date;
  timezone: string;
  sendRate?: number;
  trackOpens: boolean;
  trackClicks: boolean;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
}

export const CampaignBuilder = () => {
  const navigate = useNavigate();
  const { saveCampaign } = useCampaigns();
  const [activeTab, setActiveTab] = useState("details");
  const [campaignData, setCampaignData] = useState<CampaignData>({
    name: "",
    subject: "",
    previewText: "",
    audienceSegments: [],
    excludedSegments: [],
    timezone: "UTC",
    trackOpens: true,
    trackClicks: true,
    status: 'draft'
  });
  const [isSaving, setIsSaving] = useState(false);

  const updateCampaignData = (data: Partial<CampaignData>) => {
    setCampaignData(prev => ({ ...prev, ...data }));
  };

  const handleNext = () => {
    const tabs = ["details", "audience", "content", "schedule", "preview"];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    if (activeTab === "details") {
      navigate("/dashboard/newsletter?tab=campaigns");
    } else {
      const tabs = ["details", "audience", "content", "schedule", "preview"];
      const currentIndex = tabs.indexOf(activeTab);
      if (currentIndex > 0) {
        setActiveTab(tabs[currentIndex - 1]);
      }
    }
  };

  const handleSaveAsDraft = async () => {
    if (!campaignData.name) {
      toast.error("Please provide a campaign name");
      setActiveTab("details");
      return;
    }
    
    setIsSaving(true);
    try {
      await saveCampaign({
        ...campaignData,
        status: 'draft'
      });
      toast.success("Campaign saved as draft");
      navigate("/dashboard/newsletter?tab=campaigns");
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast.error("Failed to save campaign");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-800">Create New Campaign</h3>
          <p className="text-sm text-slate-500">Configure your email campaign</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="border-slate-200"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button 
            variant="outline" 
            className="border-slate-200"
            onClick={handleSaveAsDraft}
            disabled={isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Save as Draft"}
          </Button>
        </div>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle>Campaign Builder</CardTitle>
          <CardDescription>Create and configure your newsletter campaign</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5 gap-2">
              <TabsTrigger value="details">Campaign Details</TabsTrigger>
              <TabsTrigger value="audience">Audience</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
              <TabsTrigger value="preview">Preview & Test</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 pt-4">
              <CampaignDetailsForm 
                campaignData={campaignData} 
                updateCampaignData={updateCampaignData} 
              />
              <div className="flex justify-end mt-6">
                <Button onClick={handleNext} className="bg-slate-900 hover:bg-slate-800">
                  Next: Audience Selection
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="audience" className="space-y-4 pt-4">
              <CampaignAudienceSelector 
                campaignData={campaignData} 
                updateCampaignData={updateCampaignData} 
              />
              <div className="flex justify-end mt-6">
                <Button onClick={handleNext} className="bg-slate-900 hover:bg-slate-800">
                  Next: Content
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="content" className="space-y-4 pt-4">
              <CampaignContentEditor 
                campaignData={campaignData} 
                updateCampaignData={updateCampaignData} 
              />
              <div className="flex justify-end mt-6">
                <Button onClick={handleNext} className="bg-slate-900 hover:bg-slate-800">
                  Next: Schedule
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-4 pt-4">
              <CampaignScheduler 
                campaignData={campaignData} 
                updateCampaignData={updateCampaignData} 
              />
              <div className="flex justify-end mt-6">
                <Button onClick={handleNext} className="bg-slate-900 hover:bg-slate-800">
                  Next: Preview & Test
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="space-y-4 pt-4">
              <CampaignPreview 
                campaignData={campaignData} 
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
